import React, { useEffect, useState, useContext, memo, useMemo } from "react";
import { Polygon, InfoWindow, MarkerClusterer, Marker } from "@react-google-maps/api";
import tinygradient from "tinygradient";
import {
  getPolygonsForIBS,
  paginatedApiInvoker,
} from "../../../../utils/webMapApiHelper";
import { WebMapContext } from "../../../../pages/sections/web-map/WebMapGoogleMap";
import { convertArrayToStringWithDelimeter } from "../../../../utils/helper";
import {
  fetchBuildingLocatorSearch,
  getIBSPolygonData,
  getOptrIBSMapData,
  getOptrIBSMapDataCluster,
  getTooltipData,
} from "../../../../services/api/mapIBS.api";
import { LABEL_RENAME, SEARCH_AREA_STATES, UNIT_MAPPING, clusterDefaultZoomLvl, googleClusterImgUrl } from "../../../../constants/WebMap";
import { SingleOperatorTooltip } from "../../common";
import { mapValueToRange } from "../../../../helpers/fuctionHelpers";

const IBSMapElements = ({
  eyeIcon,
  operator,
  connection_type,
  scoreRangeFrom = 0,
  scoreRangeTo = 100,
  attr,
  aggr,
  names = [],
  types = [],
  apiTrigger,
  selectedTableBuild,
  gradientAttr = [],
  attrLimits,
  attrTypes,
  isIBSChecked,
  permittedOperators
}) => {
  const [ibsPolygons, setIBSPolygons] = useState([]);
  const [polygonData, setPolygonData] = useState([]);
  const [tableBuild, setTableBuild] = useState();
  const [tooltipData, setTooltipData] = useState();
  const [initLoad, setInitLoad] = useState(true);
  const [ibsOptData, setIBSOptData] = useState([]);
  const [isLoadingPolygons, setIsLoadingPolygons] = useState(false);

  const {
    appliedFilters,
    geohashNE,
    geohashSW,
    getBbox,
    mapZoomLevel,
    geohashZoom,
    boundsChanged,
    storeLastDataLoadedBounds,
    setSearchAreaBtn,
    searchAreaBtn,
    setApisOngoing,
    ibsConfig: ibsConfigBasedOnCT,
    mapRef,
    setIBSLegendData,
    downloadData,
    setDownloadData,
  } = useContext(WebMapContext);

  const { countryCode, year, quarter, level, levelVal } = appliedFilters;

  const common_cond = useMemo(() => {
    return geohashZoom && mapZoomLevel >= geohashZoom && connection_type && aggr && attr && !selectedTableBuild;
  }, [geohashZoom, mapZoomLevel, selectedTableBuild, connection_type, attr, aggr]);

  const ibsConfig = useMemo(() => {
    return ibsConfigBasedOnCT[connection_type];
  }, [connection_type, ibsConfigBasedOnCT]);

  useEffect(() => {
    resetData();
  }, [countryCode, year, quarter]);

  useEffect(() => {
    const fetchData = async () => {
      if (!ibsOptData?.length && mapZoomLevel >= clusterDefaultZoomLvl && mapZoomLevel < geohashZoom) {
        const res = await loadIBSMapDataCluster();
        setIBSOptData([])
        setIBSOptData(res);
      }
      if (!ibsPolygons?.length && mapZoomLevel >= clusterDefaultZoomLvl && !isIBSChecked) {
        drawOnMap(true);
        storeLastDataLoadedBounds();
      }
    };

    fetchData();
  }, [mapZoomLevel]);

  useEffect(() => {
    const fetchData = async () => {
      if (mapZoomLevel >= clusterDefaultZoomLvl && mapZoomLevel < geohashZoom) {
        const res = await loadIBSMapDataCluster();
        setIBSOptData([])
        setIBSOptData(res);
      }
    };

    fetchData();
  }, [attr, connection_type, aggr, operator]);

  useEffect(() => {
    if (geohashZoom && mapZoomLevel < geohashZoom) resetData();
  }, [mapZoomLevel, geohashZoom]);

  useEffect(() => {
    setInitLoad(true);
  }, [year, quarter, mapZoomLevel, geohashZoom, boundsChanged, selectedTableBuild]);

  useEffect(() => {
    if (common_cond) {
      drawOnMap(polygonData.length === 0 || initLoad ? true : false); // no need to fetch building polys if already exists
      storeLastDataLoadedBounds();
      setInitLoad(false);
    }
  }, [level, levelVal, geohashSW, geohashZoom, boundsChanged, selectedTableBuild, apiTrigger]);

  useEffect(() => {
    if (common_cond && searchAreaBtn === SEARCH_AREA_STATES.FETCH) {
      drawOnMap(true);
      storeLastDataLoadedBounds();
    }
    // eslint-disable-next-line
  }, [searchAreaBtn]);

  useEffect(() => {
    if (selectedTableBuild) {
      drawSelectedBuild();
    } else setTableBuild();
  }, [selectedTableBuild, attr]); // attr for detect color change

  useEffect(() => {
    if (tooltipData) {
      setTooltipData();
    }
  }, [level, levelVal, geohashSW, apiTrigger]);

  useEffect(() => {
    if (eyeIcon && ibsConfig && attr) generateIBSLegend();
    else setIBSLegendData([]);
  }, [eyeIcon, ibsConfig, attr, attrLimits, apiTrigger, gradientAttr]);

  const resetData = () => {
    setPolygonData([]);
    setIBSPolygons([]);
    setTooltipData();
  };

  const fitPolygonBounds = (builds) => {
    if (mapZoomLevel < 12 || !builds?.length) return;
    if (mapRef.current) {
      const buildPoints = builds.map(x => x.poly);
      const bounds = new window.google.maps.LatLngBounds();
      buildPoints.forEach((polygon) => {
        polygon.forEach((latLng) => bounds.extend(latLng));
      });
      mapRef.current.fitBounds(bounds);
    }
  };

  const loadPolygonData = async () => {
    setPolygonData([]);
    const { neBound, swBound } = getBbox;
    const northEast = convertArrayToStringWithDelimeter(
      geohashNE.length ? geohashNE : neBound,
      "_"
    );
    const southWest = convertArrayToStringWithDelimeter(
      geohashSW.length ? geohashSW : swBound,
      "_"
    );

    const polygonResponse = await paginatedApiInvoker(
      (pageSize, page) =>
        getIBSPolygonData(
          {
            country: countryCode,
            ...(level && level !== "geoHashLevel" && {
              level:
                level === "adminLevel1"
                  ? "adm1"
                  : level === "adminLevel2"
                    ? "adm2"
                    : "adm3",
            }),
            ...(levelVal && level !== "geoHashLevel" && { levelVal }),
            northEast,
            southWest,
          },
          pageSize,
          page,
          true
        ),
      20000,
      null,
      null,
      null,
      true
    );
    setPolygonData(polygonResponse);
    return polygonResponse;
  };

  const loadIBSMapData = async () => {
    let responseData = [];
    const { neBound, swBound } = getBbox;
    const buildingType = types.length
      ? convertArrayToStringWithDelimeter(types, "_")
      : null;
    const buildingName = names.length
      ? convertArrayToStringWithDelimeter(names, "_")
      : null;
    const northEast = convertArrayToStringWithDelimeter(
      geohashNE.length ? geohashNE : neBound,
      "_"
    );
    const southWest = convertArrayToStringWithDelimeter(
      geohashSW.length ? geohashSW : swBound,
      "_"
    );
    const payload = {
      country: countryCode,
      quarter: `${quarter} ${year}`,
      ...(level &&
        level !== "geoHashLevel" && {
        level:
          level === "adminLevel1"
            ? "adm1"
            : level === "adminLevel2"
              ? "adm2"
              : "adm3",
      }),
      ...(levelVal && level !== "geoHashLevel" && { levelVal }),
      operator,
      ct: connection_type,
      scoreFrom: scoreRangeFrom,
      scoreTo: scoreRangeTo,
      type: buildingType,
      name: buildingName,
      northEast,
      southWest,
      ...(attr && attr !== "Score" && { attr }),
      ...(aggr && { aggr }),
    };

    await paginatedApiInvoker(
      (pageSize, page) => getOptrIBSMapData(payload, null, pageSize, page, true),
      20000,
      null,
      (data) => {
        if (data) responseData = [...responseData, ...data];
      },
      (data) => {
        setDownloadData({
          ...downloadData,
          ibsOperator: payload,
        });
      },
      true
    );
    if (attr !== "Score" && gradientAttr.includes(attr) && attrLimits) {
      for (const itm of responseData) {
        const norm = mapValueToRange(itm.val, attrLimits.min, attrLimits.max);
        itm.val = norm;
      }
    }
    return responseData;
  }

  const loadIBSMapDataCluster = async () => {
    let responseData = [];
    const { neBound, swBound } = getBbox;
    const buildingType = types.length
      ? convertArrayToStringWithDelimeter(types, "_")
      : null;
    const buildingName = names.length
      ? convertArrayToStringWithDelimeter(names, "_")
      : null;
    const northEast = convertArrayToStringWithDelimeter(
      geohashNE.length ? geohashNE : neBound,
      "_"
    );
    const southWest = convertArrayToStringWithDelimeter(
      geohashSW.length ? geohashSW : swBound,
      "_"
    );
    await paginatedApiInvoker(
      (pageSize, page) =>
        getOptrIBSMapDataCluster(
          {
            country: countryCode,
            quarter: `${quarter} ${year}`,
            ...(level && level !== "geoHashLevel" && {
              level:
                level === "adminLevel1"
                  ? "adm1"
                  : level === "adminLevel2"
                    ? "adm2"
                    : "adm3",
            }),
            ...(levelVal && level !== "geoHashLevel" && { levelVal }),
            operator,
            ct: connection_type,
            scoreFrom: scoreRangeFrom,
            scoreTo: scoreRangeTo,
            type: buildingType,
            name: buildingName,
            northEast,
            southWest,
            ...(attr && attr !== "Score" && { attr }),
            ...(aggr && { aggr })
          },
          pageSize,
          page,
          true
        ),
      20000,
      null,
      (data) => { if (data) responseData = [...responseData, ...data]; },
      null,
      true
    );
    if (attr !== "Score" && gradientAttr.includes(attr) && attrLimits) {
      for (const itm of responseData) {
        const norm = mapValueToRange(itm.val, attrLimits.min, attrLimits.max);
        itm.val = norm;
      }
    }
    return responseData;
  }

  const drawOnMap = async (fetchPolygons) => {
    setIBSPolygons([]);
    setTooltipData();
    let ibsPoly = [];
    setIsLoadingPolygons(true)
    setApisOngoing(count => count + 1);
    const detailResponse = await loadIBSMapData();
    if (fetchPolygons) {
      ibsPoly = await loadPolygonData();
    } else {
      ibsPoly = polygonData;
    }
    if (!appliedFilters.level) setSearchAreaBtn(SEARCH_AREA_STATES.ENABLED);
    const ibsData = getPolygonsForIBS(detailResponse, ibsPoly);
    if (names.length) fitPolygonBounds(ibsData);
    setIBSPolygons(ibsData);
    setIsLoadingPolygons(false)
    setApisOngoing(count => count - 1);
  };

  const drawSelectedBuild = async () => {
    setTooltipData();
    setApisOngoing(count => count + 1);
    try {
      const { data } = await getTooltipData(
        {
          country: countryCode,
          quarter: `${quarter} ${year}`,
          operator,
          ct: connection_type,
          id: selectedTableBuild._id,
          ...(attr !== "Score" && { attr }),
          aggr,
          poly: true
        });
      if (data && data.polygon) {
        const path = data.polygon?.coordinates[0]?.map(x => ({ lat: x[1], lng: x[0] }));
        setTableBuild({ path, data: selectedTableBuild });
        mapRef.current.setCenter({ lat: selectedTableBuild.lt, lng: selectedTableBuild.ln });
        mapRef.current.setZoom(17);
        setApisOngoing(count => count - 1);
      }
    } catch (e) {
      console.log('failed drawing table building on map', e)
    }
  }

  const colorsSection = useMemo(() => {
    if (ibsConfig) {
      const section = ibsConfig?.[0]?.content.find(x => x.type === attr);
      return section ? section.values : ibsConfig?.[0]?.content[0].values;
    }
    return null;
  }, [ibsConfig, attr]);

  const gradient = useMemo(() => {
    if (gradientAttr?.includes(attr) && colorsSection && "colorGradient" in colorsSection[0]) {
      return tinygradient(colorsSection[0].colorGradient);
    }
    return tinygradient([
      { color: '#B3B3B3', pos: 0 },
      { color: '#E8D166', pos: 0.5 },
      { color: '#D64550', pos: 1 }
    ]);
  }, [colorsSection, attr, gradientAttr]);

  const getPolygonColor = (ibsData) => {
    if (!gradientAttr.includes(attr)) {
      const colorObj = colorsSection?.find(x => x.colorText === ibsData.ibs.label);
      return colorObj?.color || 'grey';
    } else {
      if (attr === "Score") return gradient?.rgbAt(Number(ibsData.ibs.score));
      else if (ibsData.ibs.val >= 0 && ibsData.ibs.val <= 1) return gradient?.rgbAt(Number(ibsData.ibs.val));
    }
    return null;
  };

  const getBuildColor = () => {
    const opData = selectedTableBuild?.ops.find(x => x.op === operator);
    if (opData) {
      if (!gradientAttr.includes(attr)) {
        const lvl = opData.m.find(x => x.a === attr && x.ag === aggr);
        const colorObj = colorsSection?.find(x => x.colorText === lvl?.l);
        return colorObj?.color || 'grey';
      } else {
        if (attr === "Score") return gradient?.rgbAt(Number(opData.s));
        const lvl = opData.m.find(x => x.a === attr && x.ag === aggr);
        if (!lvl || !attrLimits) return null;
        const norm = mapValueToRange(lvl.v, attrLimits.min, attrLimits.max);
        if (norm < 0 || norm > 1) return null;
        return gradient?.rgbAt(Number(norm));
      }
    }
    return "grey";
  };

  const onClickBuild = async (id) => {
    setTooltipData();
    try {
      const { data } = await getTooltipData(
        {
          country: countryCode,
          quarter: `${quarter} ${year}`,
          operator,
          ct: connection_type,
          id,
          ...(attr !== "Score" && { attr }),
          aggr
        });
      if (data) {
        const operatorData = permittedOperators?.find(x => x.operator === data?.op);
        setTooltipData({
          operator: data?.op,
          opIcon: operatorData?.icon,
          operatorName: operatorData?.name,
          isAnonymized: operatorData?.isAnonymized,
          body: {
            "OSM Id": id,
            "Building Type": data.type,
            Latitude: operatorData?.isAnonymized
              ? data.geoPoint.coordinates[1]
                ?.toFixed(5)
                ?.replace(/^\d+/, (match) => 'x'.repeat(match.length))
                ?.replace(/\.\d+/, (match) => `.${'x'.repeat(match.length - 1)}`)
              : data.geoPoint.coordinates[1]?.toFixed(5),
            Longitude: operatorData?.isAnonymized
              ? data.geoPoint.coordinates[0]
                ?.toFixed(5)
                ?.replace(/^\d+/, (match) => 'x'.repeat(match.length))
                ?.replace(/\.\d+/, (match) => `.${'x'.repeat(match.length - 1)}`)
              : data.geoPoint.coordinates[0]?.toFixed(5),
            Area: parseFloat(data.area)?.toFixed(3) + " m",
            "Total Devices": data.tot_d?.toLocaleString("en-US"),
            "Total Samples": data.tot_r?.toLocaleString("en-US"),
            Score: data.f_score,
            ...(attr !== "Score" && {
              ...(aggr !== "SUM" && { "Aggregation Type": data.aggr }),
              [`${LABEL_RENAME[data.attr] ? LABEL_RENAME[data.attr] : data.attr}`]: `${data.val.toLocaleString("en-US")}${UNIT_MAPPING[data.attr] && data.val !== "No Data" ? ` ${UNIT_MAPPING[data.attr]}` : ''}` // Appending unit if exists
            }),
            "Service Level": data.s_lvl,
          },
          point: { lat: data.geoPoint.coordinates[1], lng: data.geoPoint.coordinates[0] },
          buildingName: data.name,
        })

      }
    } catch (e) {
      console.log('Error getting IBS tooltip: ', e)
    }
  };

  const generateIBSLegend = () => {
    let ibsColorValuesArr = [];
    const section = ibsConfig?.[0]?.content.find(x => x.type === attr) || ibsConfig?.[0]?.content[0];
    if (!section) return;
    if (!gradientAttr.includes(attr)) {
      section.values.forEach((contItem) => {
        let colorObj = {};
        colorObj.text = contItem?.text;
        colorObj.markerType = contItem?.colorType;
        colorObj.markerColor = contItem?.color;
        ibsColorValuesArr.push(colorObj);
      });
      setIBSLegendData([
        {
          title: section.title,
          values: ibsColorValuesArr,
        },
      ]);
    } else {
      let minPos = Number.POSITIVE_INFINITY;
      let maxPos = Number.NEGATIVE_INFINITY;
      const gradientArr = section.values[0]?.colorGradient.map((obj) => {
        const { _id, ...rest } = obj
        const pos = obj.pos;
        if (pos < minPos) {
          minPos = pos;
        }
        if (pos > maxPos) {
          maxPos = pos;
        }
        return rest;
      });
      setIBSLegendData([
        {
          title: section?.title,
          gradient: {
            colors: gradientArr, min: parseInt(attr === "Score" ? minPos : attrLimits?.min), max: parseInt(attr === "Score" ? maxPos : attrLimits?.max).toLocaleString("en-US")
          },
        },
      ]);
    }
  };

  const getPolygonCentroid = (data) => {
    return { lng: data[0], lat: data[1] };
  };

  return (
    <>
      {mapZoomLevel >= geohashZoom && (tableBuild || ibsPolygons.length > 0) || ibsOptData?.length ? (
        <>
          {tableBuild ? (
            <div className="data-on-map">
              <Polygon
                path={tableBuild.path}
                options={{
                  fillColor: getBuildColor(),
                  fillOpacity: 0.6,
                  strokeColor: getBuildColor(),
                  strokeOpacity: 0.7,
                  strokeWeight:
                    mapZoomLevel > 15 ? 1.8 : mapZoomLevel > 14 ? 1.4 : 0.8,
                  clickable: true,
                  draggable: false,
                  editable: false,
                  geodesic: false,
                  zIndex: 1,
                }}
                onClick={() => {
                  onClickBuild(tableBuild.data._id);
                }}
              />
            </div>
          ) : (
            <div className="data-on-map">
              {ibsPolygons?.map((data, index) => {
                const color = getPolygonColor(data);
                return color ? (
                  <Polygon
                    path={data.poly}
                    key={index}
                    options={{
                      fillColor: color,
                      fillOpacity: 0.5,
                      strokeColor: color,
                      strokeOpacity: 0.7,
                      strokeWeight:
                        mapZoomLevel > 15 ? 1.8 : mapZoomLevel > 14 ? 1.4 : 0.8,
                      clickable: true,
                      draggable: false,
                      editable: false,
                      geodesic: false,
                      zIndex: 1,
                    }}
                    onClick={() => {
                      onClickBuild(data.ibs.id);
                    }}
                  />
                ) : (
                  <></>
                );
              })}
            </div>
          )}
          {!isLoadingPolygons && ibsPolygons?.length === 0 &&
            ibsOptData?.length &&
            mapZoomLevel >= clusterDefaultZoomLvl && (
              <MarkerClusterer options={{ imagePath: googleClusterImgUrl }}>
                {(clusterer) =>
                  ibsOptData.map((data, index) => {
                    const centroid = getPolygonCentroid(data.geoPoint.coordinates);

                    return centroid ? (
                      <Marker
                        key={index}
                        position={{ lat: centroid.lat, lng: centroid.lng }}
                        clusterer={clusterer}
                        options={{
                          clickable: false,
                        }}
                        onLoad={(marker) => {
                          if (marker) {
                            marker.setVisible(false);
                          }
                        }}
                      />
                    ) : (
                      <></>
                    );
                  })
                }
              </MarkerClusterer>
            )}
          {tooltipData && (
            <InfoWindow
              position={tooltipData.point}
              onCloseClick={() => {
                setTooltipData();
              }}
              options={{ disableAutoPan: true }}
            >
              <SingleOperatorTooltip data={tooltipData} attr={attr} attrTypes={attrTypes} />
            </InfoWindow>
          )}
        </>
      ) : (
        <></>
      )}
    </>
  );
};

export default memo(IBSMapElements);
